import { useCallback } from 'react';
import { useCampaignManagement } from './useCampaignManagement';
import { CreateCampaignPayload, CreateAssetGroupPayload } from '../redux/campaign/campaignSlice';

interface Clinician {
  id: string;
  name: string;
  first_name: string;
  last_name: string;
  email: string;
  enabled: boolean;
  specialties: string[];
  id_practice: string;
  profile?: {
    profileImage?: string;
    bio?: string;
  };
}

export const useClinicianStatusManagement = () => {
  const {
    createCampaign,
    createAssetGroup,
    isLoading,
    error
  } = useCampaignManagement();

  const handleClinicianActivation = useCallback(async (clinician: Clinician) => {
    try {
      // Create campaign when clinician is activated
      const campaignData: CreateCampaignPayload = {
        name: `${clinician.name || `${clinician.first_name} ${clinician.last_name}`} - Individual Campaign`,
        practice_id: clinician.id_practice,
        daily_budget: 50, // Default budget for individual clinician campaigns
        start_date: new Date().toISOString().split('T')[0], // Today's date
        target_audience: {
          locations: [], // Will be populated from practice data
          demographics: {
            age_ranges: ['18-25', '26-35', '36-45', '46-55', '56-65', '66+'],
            genders: ['Female', 'Male', 'Non-Binary']
          },
          interests: ['Mental Health', 'Therapy', 'Counseling', 'Wellness']
        }
      };

      const campaign = await createCampaign(campaignData);

      // Create asset group for the clinician campaign
      const assetGroupData: CreateAssetGroupPayload = {
        campaign_id: campaign.id!,
        name: `${clinician.name || `${clinician.first_name} ${clinician.last_name}`} - Asset Group`,
        headlines: [
          `${clinician.name || `${clinician.first_name} ${clinician.last_name}`} - Licensed Therapist`,
          `Expert Mental Health Care`,
          `Professional Therapy Services`,
          `Same Day Appointments Available`,
          `Insurance Accepted`
        ],
        descriptions: [
          `Professional therapy services with ${clinician.name || `${clinician.first_name} ${clinician.last_name}`}. Licensed clinician accepting insurance.`,
          `Expert mental health care in a supportive environment. Book your session today.`,
          `Licensed therapist available for individual and group sessions.`
        ],
        images: [
          // Use clinician profile image if available, otherwise default therapy image
          clinician.profile?.profileImage || 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800',
          'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800'
        ],
        final_urls: [
          `${window.location.origin}/clinician/${clinician.id}`,
          `${window.location.origin}/clinician/${clinician.id}/book`
        ]
      };

      await createAssetGroup(assetGroupData);

      return { success: true, campaign };
    } catch (error) {
      console.error('Failed to create campaign for clinician activation:', error);
      throw error;
    }
  }, [createCampaign, createAssetGroup]);

  const handleClinicianDeactivation = useCallback(async () => {
    try {
      // When clinician is deactivated, we would typically pause or stop their campaigns
      // This would require fetching existing campaigns for the clinician
      // For now, we'll just return success as the campaign management
      // would be handled separately through the campaign management interface
      
      return { success: true, message: 'Clinician deactivated. Campaigns should be managed separately.' };
    } catch (error) {
      console.error('Failed to handle clinician deactivation:', error);
      throw error;
    }
  }, []);

  const handleClinicianStatusChange = useCallback(async (clinician: Clinician, newStatus: 'active' | 'inactive') => {
    try {
      const currentStatus = clinician.enabled ? 'active' : 'inactive';
      if (newStatus === 'active' && currentStatus !== 'active') {
        return await handleClinicianActivation(clinician);
      } else if (newStatus === 'inactive' && currentStatus === 'active') {
        return await handleClinicianDeactivation();
      }
      
      return { success: true, message: 'Status updated successfully' };
    } catch (error) {
      console.error('Failed to handle clinician status change:', error);
      throw error;
    }
  }, [handleClinicianActivation, handleClinicianDeactivation]);

  return {
    handleClinicianActivation,
    handleClinicianDeactivation,
    handleClinicianStatusChange,
    isLoading,
    error
  };
}; 